import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import { ActionType, RootState } from "../reducer";
import {
  AiEvent,
  AiEventType,
  ConnectionStatus,
} from "../types/ai-event";
import { useSelectedNotebookPageId } from "./page";
import { useAiConnection } from "../connection/AiConnectionProvider";
import {
  HISTORY_NOT_SENT_MESSAGE,
  NOT_SENT_MESSAGE,
  RESUME_NOT_SENT_MESSAGE,
} from "../connection/sessionContinuity";
import { markStart } from "../connection/perfMarks";
import type { AiHistoryState, QueuedAiMessage } from "../slices/ui";

// The History Page size the agent-service contract fixes (agent-chat-speed Decision 1).
export const HISTORY_PAGE_SIZE = 20;

// Panel visibility
export function useAiPanelOpen(): [boolean, (open: boolean) => void] {
  const dispatch = useDispatch();
  const open = useSelector((state: RootState) => state.ui.aiPanelOpen);
  const setOpen = useCallback(
    (aiPanelOpen: boolean) =>
      dispatch({ type: ActionType.SET_AI_PANEL_OPEN, aiPanelOpen }),
    [dispatch]
  );
  return [open, setOpen];
}

export function useToggleAiPanel(): () => void {
  const [open, setOpen] = useAiPanelOpen();
  return useCallback(() => setOpen(!open), [open, setOpen]);
}

// Panel fullscreen mode
export function useAiPanelFullscreen(): [boolean, (fullscreen: boolean) => void] {
  const dispatch = useDispatch();
  const fullscreen = useSelector((state: RootState) => state.ui.aiPanelFullscreen);
  const setFullscreen = useCallback(
    (aiPanelFullscreen: boolean) =>
      dispatch({ type: ActionType.SET_AI_PANEL_FULLSCREEN, aiPanelFullscreen }),
    [dispatch]
  );
  return [fullscreen, setFullscreen];
}

// Events
export function useAiEvents(): AiEvent[] {
  return useSelector((state: RootState) => state.ui.aiEvents);
}

// History Page state of the open chat (cursor / hasMore / loading / error / loaded).
export function useAiHistory(): AiHistoryState {
  return useSelector((state: RootState) => state.ui.aiHistory);
}

export function useAddAiEvent(): (event: AiEvent) => void {
  const dispatch = useDispatch();
  return useCallback(
    (event: AiEvent) =>
      dispatch({ type: ActionType.ADD_AI_EVENT, event }),
    [dispatch]
  );
}

export function useClearAiEvents(): () => void {
  const dispatch = useDispatch();
  return useCallback(
    () => dispatch({ type: ActionType.CLEAR_AI_EVENTS }),
    [dispatch]
  );
}

// Connection status
export function useAiConnectionStatus(): [
  ConnectionStatus,
  (status: ConnectionStatus) => void
] {
  const dispatch = useDispatch();
  const status = useSelector(
    (state: RootState) => state.ui.aiConnectionStatus
  );
  const setStatus = useCallback(
    (status: ConnectionStatus) =>
      dispatch({
        type: ActionType.SET_AI_CONNECTION_STATUS,
        status,
      }),
    [dispatch]
  );
  return [status, setStatus];
}

// Send message over WebSocket
export function useSendMessage(): (content: string) => boolean {
  const addEvent = useAddAiEvent();
  const [selected] = useSelectedNotebookPageId();
  // last visited notebook page or undefined
  const pageId = selected || undefined;
  const { send, sendCreate } = useAiConnection();

  return useCallback(
    (content: string) => {
      markStart("ai-send-click");
      // Add user message to local events immediately
      addEvent({
        type: AiEventType.USER_MESSAGE,
        content,
        pageId,
        timestamp: Date.now(),
      });

      // U.3 — explicit session.create (carrying the picked devcontainer)
      // fires immediately before the first user.message; idempotent, so a
      // redundant call on later sends is a no-op.
      const created = sendCreate();
      // Decision 5 — a new chat's first message shows "Starting workspace…" at once, before any
      // server frame; the server's session.provisioning / status_progress / status_running follow.
      if (created === "sent") {
        addEvent({ type: AiEventType.STATUS_PROVISIONING, timestamp: Date.now() });
      }
      const sent = created !== "dropped" && send({ type: "user.message", content, pageId });
      if (!sent) {
        addEvent({ type: AiEventType.SESSION_ERROR, error: NOT_SENT_MESSAGE, timestamp: Date.now() });
      }
      // The composer's queue waits for the service's status_running only after an accepted send.
      return sent;
    },
    [addEvent, pageId, send, sendCreate]
  );
}

// Messages sent while a turn runs wait here (state.ui.aiQueued) and the composer sends them one per turn.
let queuedSeq = 0;

export function useAiQueue() {
  const dispatch = useDispatch();
  const queued: QueuedAiMessage[] = useSelector((state: RootState) => state.ui.aiQueued);
  const enqueue = useCallback(
    (content: string) =>
      dispatch({ type: ActionType.ENQUEUE_AI_MESSAGE, id: `queued-${Date.now()}-${++queuedSeq}`, content }),
    [dispatch]
  );
  const dequeue = useCallback(() => dispatch({ type: ActionType.DEQUEUE_AI_MESSAGE }), [dispatch]);
  const cancel = useCallback((id: string) => dispatch({ type: ActionType.CANCEL_AI_QUEUED, id }), [dispatch]);
  return { queued, enqueue, dequeue, cancel };
}

// Interrupt agent
export function useInterruptAgent(): () => void {
  const { send } = useAiConnection();
  return useCallback(() => {
    send({ type: "user.interrupt" });
  }, [send]);
}

// Move a running Bash call to the background (the Background button of ToolCallCard). The bridge answers by acting at once,
// not through the turn inbox (omega-ai-agent-service pages/agent-service/websocket-protocol.html, tool.background).
export function useBackgroundTool(): (toolUseId: string) => void {
  const { send } = useAiConnection();
  return useCallback((toolUseId: string) => {
    send({ type: "tool.background", toolUseId });
  }, [send]);
}

// Reset the session — interrupts the agent, wipes server-side memory,
// and clears the on-screen events. Fires from the trash-can icon.
// Same as New chat (interrupt, reset, clear, unbind) — one implementation so they cannot drift.
export function useResetSession(): () => void {
  return useOpenNewChat();
}

// New chat — lets go of any running Run (it keeps going in the workspace; cas-mobile-shell D4), resets the
// server-side session so the next message starts a brand-new opencode session, and clears the UI.
export function useNewChat(): () => void {
  const dispatch = useDispatch();
  const clearEvents = useClearAiEvents();
  const { send } = useAiConnection();
  return useCallback(() => {
    send({ type: "user.reset" });
    // A chat left mid-Run never sends this socket its status_idle, which is what refreshes the list after
    // a Run (AiConnectionProvider listAfterIdleRef). Without this its row would be missing until a reload.
    send({ type: "session.list" });
    clearEvents();
    dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: null });
    dispatch({ type: ActionType.SET_ACTIVE_SESSION_ID, sessionId: null });
  }, [clearEvents, dispatch, send]);
}

// On a chat route the URL is the source of truth (ChatPage acts on it); anywhere else (the AI panel over another page) the hooks act directly.
function useOnChatRoute(): boolean {
  const chat = useMatch("/chat/:chatId");
  const fresh = useMatch("/newchat");
  return chat !== null || fresh !== null;
}

export function useOpenNewChat(): () => void {
  const navigate = useNavigate();
  const onChatRoute = useOnChatRoute();
  const newChat = useNewChat();
  return useCallback(() => {
    if (onChatRoute) navigate("/newchat");
    else newChat();
  }, [navigate, newChat, onChatRoute]);
}

export function useOpenChat(): (sessionId: string) => void {
  const navigate = useNavigate();
  const onChatRoute = useOnChatRoute();
  const resume = useResumeSession();
  return useCallback((sessionId: string) => {
    if (onChatRoute) navigate(`/chat/${sessionId}`);
    else resume(sessionId);
  }, [navigate, onChatRoute, resume]);
}

// Start this chat's server session over, for a provider change (the refusal banner's "Switch this chat to OpenRouter").
// The bridge picks a Session's provider once, when it creates it (omega-ai-agent-service src/server.ts ensureSession ->
// src/session/provider.ts resolveSessionProvider); a later user.message reuses that Session, and after a refused create
// the client would skip session.create and get the bridge's lazy fallback, which resolves the Account's provider again.
// user.reset makes the next send carry a fresh session.create with the override (AiConnectionProvider createdRef). With no
// Bound Session nothing was kept, so the transcript stays and the refused message can be sent again; a Bound Session is a
// conversation on the old provider that the new one cannot continue, so it is let go exactly as New chat does.
export function useRestartSession(): () => void {
  const { send } = useAiConnection();
  const newChat = useNewChat();
  const activeSessionId = useSelector((state: RootState) => state.ui.activeSessionId);
  return useCallback(() => {
    if (activeSessionId) newChat();
    else send({ type: "user.reset" });
  }, [activeSessionId, newChat, send]);
}

// List sessions — sends session.list request over WebSocket
export function useListSessions(): () => void {
  const { send } = useAiConnection();
  return useCallback(() => {
    send({ type: "session.list" });
  }, [send]);
}

// Resume a session — clears the current transcript first so only the resumed session's newest
// History Page shows, marks it active, shows "Starting workspace…" at once (Decision 5), then asks
// for the resume over the WebSocket. The server's session.replay replaces the transcript in one
// update; its status_idle ends the hold.
export function useResumeSession(): (sessionId: string) => void {
  const dispatch = useDispatch();
  const { send } = useAiConnection();
  // Mid-Run too: the bridge lets go of the running Run and resumes this chat (cas-mobile-shell D1).
  return useCallback((sessionId: string) => {
    markStart("ai-resume-click");
    dispatch({ type: ActionType.CLEAR_AI_EVENTS });
    dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: "resuming" });
    dispatch({ type: ActionType.SET_ACTIVE_SESSION_ID, sessionId });
    dispatch({ type: ActionType.ADD_AI_EVENT, event: { type: AiEventType.STATUS_PROVISIONING, timestamp: Date.now() } });
    if (!send({ type: "session.resume", sessionId })) {
      dispatch({ type: ActionType.SET_AI_CHAT_LOADING, loading: null });
      dispatch({
        type: ActionType.ADD_AI_EVENT,
        event: { type: AiEventType.SESSION_ERROR, error: RESUME_NOT_SENT_MESSAGE, timestamp: Date.now() },
      });
    }
  }, [dispatch, send]);
}

// Load the next older History Page of the open chat (Decision 1): session.history with the
// History Cursor the last page returned. A no-op without a cursor, with nothing older, or while a
// request is out; the list header shows loading / error / "Beginning of conversation".
export function useLoadOlderHistory(): () => void {
  const dispatch = useDispatch();
  const { send } = useAiConnection();
  const sessionId = useSelector((state: RootState) => state.ui.activeSessionId);
  const history = useAiHistory();
  return useCallback(() => {
    if (!sessionId || history.cursor === null || !history.hasMore || history.loading) return;
    dispatch({ type: ActionType.SET_AI_HISTORY_LOADING });
    const sent = send({ type: "session.history", sessionId, before: history.cursor, limit: HISTORY_PAGE_SIZE });
    if (!sent) dispatch({ type: ActionType.SET_AI_HISTORY_ERROR, error: HISTORY_NOT_SENT_MESSAGE });
  }, [dispatch, send, sessionId, history]);
}
